import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Stack,
    Paper,
    Card,
    CardContent,
    useTheme,
    alpha,
    Collapse,
    Divider,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandshakeIcon from '@mui/icons-material/Handshake';

import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import BannerSection from '../components/BannerSection';
import axiosInstance from '../services/axiosInstance';

interface HomePageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

const AboutPage = ({ mode, setMode }: HomePageProps) => {
    const navigate = useNavigate();
    const theme = useTheme();
    // State for Read More toggle
    const [showMore, setShowMore] = useState<boolean>(false);

    const [loading, setLoading] = useState(false);
    const [adSpaceBanner, setAdSpaceBanner] = useState<any[]>([]);




    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.post(
                    "/CustomerVehicle/VehicleMultipleDataList",
                    {
                        adSpaceData: true,
                    },
                    { headers: { "Content-Type": "application/json" } }
                );

                const result = response.data as {
                    responseCode: string;
                    responseMessage?: string;
                    responseData?: {
                        adSpaceData?: any[];
                    };
                };

                if (result.responseCode === "00" && result.responseData) {

                    // 🔹 filter only homepage banner(s)
                    const homePageBanner = (result.responseData.adSpaceData || []).filter(
                        (ad: any) => ad.adPositionCode === "homePage-Banner"
                    );
                    setAdSpaceBanner(homePageBanner);
                } else {
                    console.log("No ad space data found");
                }
            } catch {
                console.log("❌ Error fetching ad space data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    return (
        <>
            <BannerSection adSpaceBanner={adSpaceBanner} />


            <Box
                sx={{
                    width: '100%',
                    maxWidth: 1200,
                    mx: 'auto',
                    mt: -4,
                    position: 'relative',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column', // Stack items vertically
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {/* BRAND PROMISE Text */}
                <Typography
                    variant="overline"
                    sx={{ fontWeight: 'bold', letterSpacing: 2, color: 'primary.main' }}
                >
                    BRAND PROMISE
                </Typography>
                <Typography
                    variant="h3"
                    fontWeight={800}
                    gutterBottom
                    sx={{
                        color: 'text.primary',
                        textAlign: 'center',
                        maxWidth: { xs: '100%', md: '80%' }, // Responsive width control
                        textWrap: 'balance',                 // Makes the lines look even
                        mx: 'auto'                           // Centers the element
                    }}
                >
                    Built to Help Businesses Make Smarter Commercial Vehicle Decisions
                </Typography>

                {/* Buttons Stack */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="center"
                // mt={3} // Removed as mb={3} on Typography handles spacing
                >
                    <Button
                        variant="contained"
                        size="large"
                        sx={{
                            bgcolor: '#FFA100',
                            color: '#000',
                            '&:hover': {
                                color: '#fff',
                                bgcolor: '#FFB733'
                            }
                        }}
                        onClick={() => navigate('/vehicle-listing')}
                    >
                        Explore Listings
                    </Button>

                    <Button
                        variant="contained"
                        sx={{
                            bgcolor: '#FFA100',
                            color: '#000',
                            '&:hover': {
                                color: '#fff',
                                bgcolor: '#FFB733'
                            }
                        }}
                        size="large"
                        component="a"
                        href="/partner-with-us"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Partner with Us
                    </Button>
                </Stack>
            </Box>

            {/* --- COMPACT HERO SECTION --- */}


            {/* --- MAIN CONTENT SECTION (Like Image Structure) --- */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Divider sx={{ mb: 6 }} />
                <Grid container spacing={6}>

                    {/* LEFT: TEXT CONTENT */}
                    <Grid size={{ xs: 12, md: 7 }} >
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Our Story & Objectives
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                            WorkHorse is Singapore’s specialised commercial vehicle platform built for
                            dealers, businesses, and buyers who want a faster, safer, and more
                            efficient way to buy and sell commercial vehicles.
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                            Whether you’re managing a single van or a full fleet, WorkHorse delivers
                            a seamless experience designed to save time, reduce operational hassle,
                            and achieve stronger results.
                        </Typography>

                        {/* --- HIDDEN CONTENT (Collapsible) --- */}
                        <Collapse in={showMore}>
                            <Box sx={{ mt: 2 }}>
                                {/* COMMITMENT */}
                                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
                                    Our Commitment
                                </Typography>
                                <Typography variant="body1" fontStyle="italic" color="text.secondary">
                                    We aim to be Singapore’s most trusted platform for commercial vehicles
                                    by connecting the right buyers with the right vehicles — efficiently,
                                    transparently, and professionally.
                                </Typography>
                                <Typography variant="body1" color="text.secondary" mt={2}>
                                    Our mission is clear: to simplify and elevate the entire commercial
                                    vehicle journey — from listing to closing the deal.
                                </Typography>

                                {/* WHY WORKHORSE */}
                                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
                                    Why WorkHorse?
                                </Typography>
                                <Typography variant="body1" color="text.secondary" mb={2}>
                                    WorkHorse focuses 100% on commercial vehicles, meaning every feature of
                                    our platform is purpose-built for the industry.
                                    <br /><br />
                                    Dealers gain stronger visibility, higher-quality enquiries, and smart
                                    tools to close deals faster, while buyers enjoy a transparent, trusted
                                    environment to find the right vehicle with confidence.
                                </Typography>
                            </Box>
                        </Collapse>

                        {/* --- TOGGLE BUTTON --- */}
                        <Button
                            color="primary"
                            onClick={() => setShowMore(!showMore)}
                            sx={{ fontWeight: 'bold', textTransform: 'none', pl: 0, mt: 2 }}
                        >
                            {showMore ? "Read Less" : "Read More"}
                        </Button>
                    </Grid>

                    {/* RIGHT: STATISTICS */}
                    <Grid size={{ xs: 12, md: 5 }} >
                        <Stack spacing={4}>
                            <Box>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        bgcolor: '#ffd400', // Red color
                                        color: 'white',
                                        borderRadius: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Vision
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        To become the go-to platform for all commercial vehicle-related matters,
                                        setting the standard for trust and efficiency in the industry.
                                    </Typography>
                                </Paper>
                            </Box>
                            <Box>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 4,
                                        height: '100%',
                                        bgcolor: '#ffc200', // Dark grey/black
                                        color: 'white',
                                        borderRadius: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Mission
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Simplify commercial vehicle ownership for businesses by creating an ecosystem
                                        of smart tools and services that allow owners to better manage their fleets.
                                    </Typography>
                                </Paper>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>

            {/* --- CORE VALUES / DIFFERENTIATORS SECTION (Grey Background) --- */}
            <Box sx={{ bgcolor: theme.palette.mode === 'light' ? '#F9FAFB' : alpha(theme.palette.background.paper, 0.5), py: 10 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom sx={{ mb: 6 }}>
                        What Makes Us Different
                    </Typography>

                    <Grid container spacing={4} mt={1}>
                        {/* ITEM 1 */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ fontSize: 40, color: '#FFa100' }}>
                                    <DirectionsCarIcon fontSize="inherit" />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        Exclusively for Commercial Vehicles
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        No passenger cars. Only vans, lorries, trucks, pickups, buses
                                        & fleet vehicles.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        {/* ITEM 2 */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ fontSize: 40, color: '#fe4119' }}>
                                    <PeopleAltIcon fontSize="inherit" />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        Built for Dealers, Businesses & Professional Buyers
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Fast listings, verified dealers, organised categories —
                                        designed for real industry needs.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        {/* ITEM 3 */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ fontSize: 40, color: '#961e77' }}>
                                    <BoltIcon fontSize="inherit" />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        Smarter Tools & Real-Time Insights
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Market trends, buyer behaviour, and pricing data to boost
                                        performance.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        {/* ITEM 4 */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ fontSize: 40, color: theme.palette.success.main }}>
                                    <VerifiedIcon fontSize="inherit" />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        Safe, Transparent & Easy to Use
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Structured verification for dealers and listings. Clear,
                                        accurate, and trustworthy.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        {/* ITEM 5 */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Box sx={{ fontSize: 40, color: '#252e9e' }}>
                                    <HandshakeIcon fontSize="inherit" />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        Growing the Industry Together
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Tools and resources that support collaboration and long-term
                                        industry growth.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Footer />
        </>
    );
};

export default AboutPage;